/*
 * =============================================================================
 * FILE HEADER — apps/studio/src/components/SettingsPage.tsx
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why                | What Changed             |
 * |---|------------------------|-------------------|--------------------|--------------------------|
 * | 1 | 2026-06-19 (Phase 3)   | Claude Sonnet 4.6 | Settings page      | Created — Profile/FAQ/Support/Donate |
 * | 2 | 2026-07-06             | Claude Sonnet 5   | AI Assist port     | Added AI Assist On/Off toggle +      |
 * |   |                        |                   | (from Plex)        | getAiAssistEnabled/setAiAssistEnabled |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - apps/studio/src/App.tsx  (<SettingsPage />)
 *
 * EXPORTS
 * -------
 * SettingsPage — full-page settings component
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. Username is persisted to localStorage key "mimiq-username".
 * 2. Support form uses mailto: — opens user's email client pre-filled.
 *    No backend required. File attachment note is shown to guide user.
 * 3. FAQ items are collapsible via local state (no global state needed).
 * 4. Donate link opens a Ko-fi / GitHub Sponsors URL in a new tab.
 * 5. getAiAssistEnabled()/setAiAssistEnabled() (added for the AI Assist
 *    recorder tab, ported from Plex) follow the exact getTheme()/applyTheme()
 *    localStorage pattern above — default true, mirrors Plex's
 *    `aiAssistEnabled` setting default.
 *
 * =============================================================================
 */

import { useState, useEffect } from "react";

const APP_VERSION = "0.3.0";
const SUPPORT_EMAIL = "jeyaprakash32@gmail.com";
const DONATE_URL = "https://ko-fi.com/mimiq";

const LS_THEME = "mimiq-theme";

export type AppTheme = "dark" | "light";

export function getTheme(): AppTheme {
  try { return (localStorage.getItem(LS_THEME) as AppTheme) ?? "dark"; } catch { return "dark"; }
}

export function applyTheme(theme: AppTheme) {
  document.documentElement.setAttribute("data-theme", theme);
  try { localStorage.setItem(LS_THEME, theme); } catch { /* ignore */ }
}

const LS_AI_ASSIST_ENABLED = "mimiq-ai-assist-enabled";

export function getAiAssistEnabled(): boolean {
  try {
    const raw = localStorage.getItem(LS_AI_ASSIST_ENABLED);
    return raw === null ? true : raw === "true";
  } catch {
    return true;
  }
}

export function setAiAssistEnabled(enabled: boolean): void {
  try { localStorage.setItem(LS_AI_ASSIST_ENABLED, String(enabled)); } catch { /* ignore */ }
}

/* ── FAQ data ──────────────────────────────────────────────────────────────── */

// FAQ is intentionally short — full docs are in the Wiki tab (? icon in sidebar).
const FAQ_ITEMS: { q: string; a: string | string[] }[] = [
  {
    q: "Where can I find detailed documentation?",
    a: "Click the ? Wiki tab in the left sidebar. It covers every command, locator strategy, CLI flags, data sources, and more — with examples.",
  },
  {
    q: "How do I connect my Android phone?",
    a: [
      "1. Enable Developer Options on your phone (Settings → About Phone → tap Build Number 7 times).",
      "2. Enable USB Debugging in Developer Options.",
      "3. Connect your phone to your PC with a USB cable.",
      "4. Open Mimiq Studio in Chrome and click Connect Phone.",
      "5. Select your phone in the Chrome dialog, tap Allow USB debugging on the phone.",
      "Tip: If your phone isn't listed, try a different USB port or cable, or run 'adb kill-server' in a terminal.",
    ],
  },
  {
    q: "Connection issue: ADB interface is busy / device not found",
    a: [
      "→ Close Android Studio, Samsung Smart Switch, or any other app that uses ADB.",
      "→ Open a terminal and run: adb kill-server",
      "→ Disconnect and reconnect the USB cable, then try connecting again.",
      "→ Try a different USB port or cable (data cable, not charge-only).",
      "→ On Windows: open Device Manager — phone must appear under 'Android Device' or 'ADB Interface'.",
    ],
  },
];

/* ── Support form ──────────────────────────────────────────────────────────── */

type SupportCategory = "Bug Report" | "Feature Request" | "General Query" | "Other";
const SUPPORT_CATEGORIES: SupportCategory[] = ["Bug Report", "Feature Request", "General Query", "Other"];

function SupportModal({ onClose }: { onClose: () => void }) {
  const [category, setCategory] = useState<SupportCategory>("General Query");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitted, setSubmitted] = useState(false);

  function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) return;
    setFiles((prev) => [...prev, ...Array.from(e.target.files!)]);
  }

  function removeFile(i: number) {
    setFiles((prev) => prev.filter((_, idx) => idx !== i));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const subj = encodeURIComponent(`[Mimiq ${category}] ${subject || "(no subject)"}`);
    const fileNote = files.length > 0
      ? `\n\n--- Attachments (${files.length} file${files.length > 1 ? "s" : ""}) ---\n${files.map((f) => `• ${f.name} (${(f.size / 1024).toFixed(1)} KB)`).join("\n")}\nPlease attach these files to this email.`
      : "";
    const body = encodeURIComponent(`Category: ${category}\n\n${message}${fileNote}`);
    window.open(`mailto:${SUPPORT_EMAIL}?subject=${subj}&body=${body}`, "_blank");
    setSubmitted(true);
  }

  return (
    <div className="support-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="support-modal">
        <div className="support-modal-header">
          <h2>Contact Support</h2>
          <button className="support-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {submitted ? (
          <div className="support-success">
            <div className="support-success-icon">✓</div>
            <h3>Your email client has opened!</h3>
            <p>A pre-filled email is ready for you to send. If you selected files, please attach them before sending.</p>
            <p className="support-success-email">Sending to: <strong>{SUPPORT_EMAIL}</strong></p>
            <button className="btn btn--primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form className="support-form" onSubmit={handleSubmit}>
            <div className="support-field">
              <label className="support-label">Category</label>
              <select className="support-select" value={category} onChange={(e) => setCategory(e.target.value as SupportCategory)}>
                {SUPPORT_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div className="support-field">
              <label className="support-label">Subject</label>
              <input
                className="support-input"
                type="text"
                placeholder="Brief summary of your query"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </div>

            <div className="support-field">
              <label className="support-label">Message</label>
              <textarea
                className="support-textarea"
                placeholder="Describe your issue or request in detail. Include steps to reproduce if reporting a bug."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                required
              />
            </div>

            <div className="support-field">
              <label className="support-label">Attachments <span className="support-label-hint">(screenshots, logs, PDFs)</span></label>
              <label className="support-file-btn">
                + Add files
                <input type="file" multiple accept="image/*,.pdf,.txt,.log,.json,.zip" onChange={handleFiles} style={{ display: "none" }} />
              </label>
              {files.length > 0 && (
                <ul className="support-file-list">
                  {files.map((f, i) => (
                    <li key={i} className="support-file-item">
                      <span className="support-file-name">{f.name}</span>
                      <span className="support-file-size">{(f.size / 1024).toFixed(1)} KB</span>
                      <button type="button" className="support-file-remove" onClick={() => removeFile(i)}>✕</button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="support-file-note">
                Files will be listed in the email. Please attach them manually in your email client before sending.
              </p>
            </div>

            <div className="support-actions">
              <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn--primary">Open Email Client →</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/* ── FAQ item ──────────────────────────────────────────────────────────────── */

function FaqItem({ item }: { item: { q: string; a: string | string[] } }) {
  const [open, setOpen] = useState(false);
  const lines = Array.isArray(item.a) ? item.a : [item.a];

  return (
    <div className={`faq-item${open ? " faq-item--open" : ""}`}>
      <button className="faq-question" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <span className="faq-arrow">{open ? "▾" : "▸"}</span>
        {item.q}
      </button>
      {open && (
        <div className="faq-answer">
          {lines.map((line, i) =>
            line === "" ? <br key={i} /> : (
              <p key={i} className={line.startsWith("→") ? "faq-line faq-line--indent" : "faq-line"}>{line}</p>
            )
          )}
        </div>
      )}
    </div>
  );
}

/* ── Project Config Panel ─────────────────────────────────────────────────── */

const LS_PROJECT_CONFIG = "mimiq-project-config";

interface StoredProjectConfig {
  project: string;
  packageName: string;
  baseUrl: string;
  stepTimeoutMs: number;
  screenshotOnFailure: boolean;
  captureEveryStep: boolean;
  showExecutionOverlay: boolean;
  captureLogsOnFailure: boolean;
  dataSourcesBasePath: string;
  envVars: string; // JSON string of Record<string, string>
}

const DEFAULT_PC: StoredProjectConfig = {
  project: "",
  packageName: "",
  baseUrl: "",
  stepTimeoutMs: 15000,
  screenshotOnFailure: true,
  captureEveryStep: false,
  showExecutionOverlay: false,
  captureLogsOnFailure: false,
  dataSourcesBasePath: "./test-data",
  envVars: "{}",
};

/** Read the captureEveryStep setting without needing React context. */
export function getCaptureEveryStep(): boolean {
  try {
    const raw = localStorage.getItem(LS_PROJECT_CONFIG);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<StoredProjectConfig>;
    return parsed.captureEveryStep ?? false;
  } catch { return false; }
}

/** Read the showExecutionOverlay setting without needing React context. */
export function getShowExecutionOverlay(): boolean {
  try {
    const raw = localStorage.getItem(LS_PROJECT_CONFIG);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<StoredProjectConfig>;
    return parsed.showExecutionOverlay ?? false;
  } catch { return false; }
}

/** Read the captureLogsOnFailure setting without needing React context. */
export function getCaptureLogsOnFailure(): boolean {
  try {
    const raw = localStorage.getItem(LS_PROJECT_CONFIG);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<StoredProjectConfig>;
    return parsed.captureLogsOnFailure ?? false;
  } catch { return false; }
}

function loadStoredConfig(): StoredProjectConfig {
  try {
    const raw = localStorage.getItem(LS_PROJECT_CONFIG);
    if (!raw) return { ...DEFAULT_PC };
    return { ...DEFAULT_PC, ...(JSON.parse(raw) as Partial<StoredProjectConfig>) };
  } catch { return { ...DEFAULT_PC }; }
}

/** Serializes the stored config to a mimiq.config.json-compatible JSON string. */
export function getProjectConfigJson(): string {
  const stored = loadStoredConfig();
  let env: Record<string, string> = {};
  try { env = JSON.parse(stored.envVars) as Record<string, string>; } catch { /* ignore */ }
  const cfg = {
    project: stored.project || undefined,
    app: stored.packageName ? { packageName: stored.packageName } : undefined,
    env: { ...(stored.baseUrl ? { BASE_URL: stored.baseUrl } : {}), ...env },
    timeouts: { stepMs: stored.stepTimeoutMs },
    reporting: { screenshotOnFailure: stored.screenshotOnFailure },
    dataSources: { basePath: stored.dataSourcesBasePath },
  };
  return JSON.stringify(cfg, null, 2);
}

function ProjectConfigPanel() {
  const [cfg, setCfg] = useState<StoredProjectConfig>(loadStoredConfig);
  const [saved, setSaved] = useState(false);
  const [envError, setEnvError] = useState("");

  function handleSave() {
    try {
      JSON.parse(cfg.envVars); // validate JSON
      setEnvError("");
    } catch {
      setEnvError("Environment variables must be valid JSON, e.g. {\"KEY\": \"value\"}");
      return;
    }
    try { localStorage.setItem(LS_PROJECT_CONFIG, JSON.stringify(cfg)); } catch { /* ignore */ }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function handleImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    file.text().then((text) => {
      try {
        const parsed = JSON.parse(text) as Record<string, unknown>;
        setCfg((prev) => ({
          ...prev,
          project: (parsed.project as string) || prev.project,
          packageName: ((parsed.app as Record<string, string>)?.packageName) || prev.packageName,
          baseUrl: ((parsed.env as Record<string, string>)?.BASE_URL) || prev.baseUrl,
          stepTimeoutMs: ((parsed.timeouts as Record<string, number>)?.stepMs) || prev.stepTimeoutMs,
          screenshotOnFailure: ((parsed.reporting as Record<string, boolean>)?.screenshotOnFailure) ?? prev.screenshotOnFailure,
          dataSourcesBasePath: ((parsed.dataSources as Record<string, string>)?.basePath) || prev.dataSourcesBasePath,
        }));
      } catch { alert("Invalid mimiq.config.json file"); }
    }).catch(() => {});
  }

  function handleExport() {
    const blob = new Blob([getProjectConfigJson()], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "mimiq.config.json"; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <section className="settings-section">
      <h2 className="settings-section-title">Project Config <span className="settings-label-badge">mimiq.config.json</span></h2>
      <div className="settings-card">
        <p className="pc-description">
          These settings apply to all test runs in this browser session. They work like a project-level <code>mimiq.config.json</code> file — the CLI can also use that file directly.
        </p>

        <div className="settings-field">
          <label className="settings-label">Project Name</label>
          <input className="settings-input" placeholder="My App Tests" value={cfg.project} onChange={(e) => setCfg({ ...cfg, project: e.target.value })} />
        </div>
        <div className="settings-field">
          <label className="settings-label">App Package</label>
          <input className="settings-input" placeholder="com.example.app" value={cfg.packageName} onChange={(e) => setCfg({ ...cfg, packageName: e.target.value })} />
        </div>
        <div className="settings-field">
          <label className="settings-label">Base URL <span className="settings-label-badge">env:BASE_URL</span></label>
          <input className="settings-input" placeholder="https://staging.example.com" value={cfg.baseUrl} onChange={(e) => setCfg({ ...cfg, baseUrl: e.target.value })} />
        </div>
        <div className="settings-field">
          <label className="settings-label">Step Timeout</label>
          <input className="settings-input" type="number" min="1000" max="120000" value={cfg.stepTimeoutMs} onChange={(e) => setCfg({ ...cfg, stepTimeoutMs: parseInt(e.target.value) || 15000 })} style={{ maxWidth: 120 }} />
          <span style={{ color: "var(--color-text-muted)", fontSize: "0.8rem" }}>ms</span>
        </div>
        <div className="settings-field">
          <label className="settings-label">Screenshot on Failure</label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={cfg.screenshotOnFailure} onChange={(e) => setCfg({ ...cfg, screenshotOnFailure: e.target.checked })} />
            <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>Capture screenshot when a step fails</span>
          </label>
        </div>
        <div className="settings-field">
          <label className="settings-label">Screenshot Every Step</label>
          <div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", marginBottom: 6 }}>
              <input type="checkbox" checked={cfg.captureEveryStep} onChange={(e) => setCfg({ ...cfg, captureEveryStep: e.target.checked })} />
              <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                Capture a PNG screenshot after every step — shown in Reports and exported HTML
              </span>
            </label>
            {cfg.captureEveryStep && (
              <div style={{
                background: "color-mix(in srgb, #f38ba8 12%, transparent)",
                border: "2px solid #f38ba8",
                borderRadius: 6,
                padding: "0.6rem 0.75rem",
                fontSize: "0.8rem",
                color: "#f38ba8",
                lineHeight: 1.5,
              }}>
                ⚠️ <strong>STORAGE WARNING</strong> — Each full-resolution screenshot is 1–5 MB (PNG at 1080×2400).
                A 10-step test will produce a <strong>~50–150 MB exported HTML file</strong>.
                Only enable this for short tests or specific debugging sessions.
                Turn it OFF when running large suites. Screenshots add <strong>~1–2 seconds per step</strong>.
              </div>
            )}
          </div>
        </div>
        <div className="settings-field">
          <label className="settings-label">Execution Click Overlay</label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={cfg.showExecutionOverlay} onChange={(e) => setCfg({ ...cfg, showExecutionOverlay: e.target.checked })} />
            <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              Show ripple on mirror canvas for each tap during execution (uses recording indicator color)
            </span>
          </label>
        </div>
        <div className="settings-field">
          <label className="settings-label">Capture Logs on Failure</label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={cfg.captureLogsOnFailure} onChange={(e) => setCfg({ ...cfg, captureLogsOnFailure: e.target.checked })} />
            <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
              Dump the accessibility tree when a step fails — shown in Reports and exported HTML
              <span style={{ color: "#f59e0b", marginLeft: 6, fontSize: "0.75rem" }}>adds ~1s per failed step</span>
            </span>
          </label>
        </div>
        <div className="settings-field">
          <label className="settings-label">Data Files Path <span className="settings-label-badge">CLI only</span></label>
          <input className="settings-input" placeholder="./test-data" value={cfg.dataSourcesBasePath} onChange={(e) => setCfg({ ...cfg, dataSourcesBasePath: e.target.value })} />
        </div>
        <div className="settings-field" style={{ flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
          <label className="settings-label">Extra Env Vars <span className="settings-label-badge">JSON</span></label>
          <textarea
            className="settings-input"
            style={{ width: "100%", minHeight: 72, fontFamily: "monospace", fontSize: "0.8rem", resize: "vertical" }}
            placeholder={'{\n  "API_KEY": "abc123",\n  "TIMEOUT": "5000"\n}'}
            value={cfg.envVars}
            onChange={(e) => setCfg({ ...cfg, envVars: e.target.value })}
          />
          {envError && <p style={{ color: "#f38ba8", fontSize: "0.75rem", margin: 0 }}>{envError}</p>}
        </div>

        <div className="settings-save-row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
          <button className="btn btn--primary" onClick={handleSave}>Save to Session</button>
          <button className="btn btn--ghost" onClick={handleExport}>↓ Download mimiq.config.json</button>
          <label className="btn btn--ghost" style={{ cursor: "pointer" }}>
            ↑ Import mimiq.config.json
            <input type="file" accept=".json" onChange={handleImport} style={{ display: "none" }} />
          </label>
          {saved && <span className="settings-save-msg">Saved!</span>}
        </div>
      </div>
    </section>
  );
}

/* ── SettingsPage ──────────────────────────────────────────────────────────── */

const LS_USERNAME = "mimiq-username";

export function SettingsPage() {
  const [username, setUsername] = useState(() => {
    try { return localStorage.getItem(LS_USERNAME) ?? ""; } catch { return ""; }
  });
  const [savedUsername, setSavedUsername] = useState(username);
  const [saveMsg, setSaveMsg] = useState("");
  const [showSupport, setShowSupport] = useState(false);
  const [theme, setTheme] = useState<AppTheme>(getTheme);
  const [aiAssistEnabled, setAiAssistEnabledState] = useState<boolean>(getAiAssistEnabled);

  useEffect(() => {
    try { setSavedUsername(localStorage.getItem(LS_USERNAME) ?? ""); } catch { /* ignore */ }
  }, []);

  function handleThemeChange(t: AppTheme) {
    setTheme(t);
    applyTheme(t);
  }

  function handleAiAssistToggle(enabled: boolean) {
    setAiAssistEnabledState(enabled);
    setAiAssistEnabled(enabled);
  }

  function handleSave() {
    try { localStorage.setItem(LS_USERNAME, username.trim()); } catch { /* ignore */ }
    setSavedUsername(username.trim());
    setSaveMsg("Saved!");
    setTimeout(() => setSaveMsg(""), 2000);
  }

  const isDirty = username.trim() !== savedUsername;

  return (
    <div className="settings-page">
      {showSupport && <SupportModal onClose={() => setShowSupport(false)} />}

      {/* ── Profile ── */}
      <section className="settings-section">
        <h2 className="settings-section-title">Profile</h2>
        <div className="settings-card">
          <div className="settings-field">
            <label className="settings-label">Version</label>
            <span className="settings-value-static">Mimiq Studio v{APP_VERSION}</span>
          </div>
          <div className="settings-field">
            <label className="settings-label">Theme</label>
            <div className="theme-toggle">
              <button
                className={`theme-btn${theme === "dark" ? " theme-btn--active" : ""}`}
                onClick={() => handleThemeChange("dark")}
              >
                🌙 Dark
              </button>
              <button
                className={`theme-btn${theme === "light" ? " theme-btn--active" : ""}`}
                onClick={() => handleThemeChange("light")}
              >
                ☀️ Light
              </button>
            </div>
          </div>
          <div className="settings-field">
            <label className="settings-label">✨ AI Assist <span className="settings-label-badge">Beta</span></label>
            <div className="theme-toggle">
              <button
                className={`theme-btn${aiAssistEnabled ? " theme-btn--active" : ""}`}
                onClick={() => handleAiAssistToggle(true)}
              >
                On
              </button>
              <button
                className={`theme-btn${!aiAssistEnabled ? " theme-btn--active" : ""}`}
                onClick={() => handleAiAssistToggle(false)}
              >
                Off
              </button>
            </div>
          </div>
          <div className="settings-field">
            <label className="settings-label">Your Name</label>
            <input
              className="settings-input"
              type="text"
              placeholder="Enter your name"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
            />
          </div>
          <div className="settings-field">
            <label className="settings-label">Email <span className="settings-label-badge">Coming soon</span></label>
            <input className="settings-input settings-input--disabled" type="email" placeholder="email@example.com" disabled />
          </div>
          <div className="settings-save-row">
            <button className="btn btn--primary" onClick={handleSave} disabled={!isDirty}>Save</button>
            {saveMsg && <span className="settings-save-msg">{saveMsg}</span>}
          </div>
        </div>
      </section>

      {/* ── Support + Donate ── */}
      <section className="settings-section">
        <h2 className="settings-section-title">Help & Community</h2>
        <div className="settings-card settings-card--actions">
          <div className="settings-action-card">
            <div className="settings-action-icon">✉</div>
            <div className="settings-action-body">
              <h3>Contact Support</h3>
              <p>Report a bug, request a feature, or just say hello. We read every message.</p>
            </div>
            <button className="btn btn--primary" onClick={() => setShowSupport(true)}>Get Support</button>
          </div>
          <div className="settings-action-card settings-action-card--donate">
            <div className="settings-action-icon">☕</div>
            <div className="settings-action-body">
              <h3>Support Mimiq</h3>
              <p>Plug in your phone and automate for free — no account, no limits, no cloud bill. If Mimiq saves you testing hours, buy us a coffee and help us build the next feature!</p>
            </div>
            <a className="btn btn--donate" href={DONATE_URL} target="_blank" rel="noopener noreferrer">Donate ♥</a>
          </div>
        </div>
      </section>

      {/* ── Project Config ── */}
      <ProjectConfigPanel />

      {/* ── FAQ ── */}
      <section className="settings-section">
        <h2 className="settings-section-title">Quick FAQ <span className="settings-label-badge">Full docs in ? Wiki sidebar tab</span></h2>
        <div className="faq-list">
          {FAQ_ITEMS.map((item, i) => <FaqItem key={i} item={item} />)}
        </div>
      </section>
    </div>
  );
}
